import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import { useNewProjectUrl } from 'domain/shared/newProject';
import useAssumptionsService from '../assumptionsService';
import { AssumptionType } from '../types';
import CreateAssumptionModal from './CreateAssumptionModal';
import UpdateAssumptionModal from './UpdateAssumptionModal';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../../shared/notifications/useReconcileProjectNotifications';

export interface AssumptionActionsContextValue {
  openCreate: () => void;
  openUpdate: (assumption: AssumptionType) => void;
  openReview: (assumption: AssumptionType) => void;
  openConfirm: (assumption: AssumptionType) => void;
}

const AssumptionActionsContext = createContext<AssumptionActionsContextValue | null>(null);

interface AssumptionActionsProviderProps {
  projectId: string;
  onSaved: (assumption: AssumptionType) => void;
  children: ReactNode;
}

export const AssumptionActionsProvider: FC<AssumptionActionsProviderProps> = ({ projectId, onSaved, children }) => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingAssumption, setEditingAssumption] = useState<AssumptionType | null>(null);

  const navigate = useNavigate();
  const { getReviewAssumptionUrl } = useNewProjectUrl();
  const { confirmAssumption } = useAssumptionsService();
  const { addMessage } = useMessageService();
  const reconcileNotifications = useReconcileProjectNotifications();

  const handleConfirm = useCallback(
    async (assumption: AssumptionType) => {
      try {
        const saved = await confirmAssumption(assumption.id);
        addMessage(new SuccessMessage('Assumption confirmed — next check in a month.'));
        await reconcileNotifications(projectId, [PROJECT_NOTIFICATION_SCOPE.ASSUMPTIONS], assumption.id).catch(
          () => undefined,
        );
        onSaved(saved);
      } catch {
        addMessage(new ErrorMessage('Failed to confirm assumption. Please try again.'));
      }
    },
    [confirmAssumption, addMessage, onSaved, reconcileNotifications, projectId],
  );

  const value = useMemo<AssumptionActionsContextValue>(
    () => ({
      openCreate: () => setIsCreateOpen(true),
      openUpdate: assumption => setEditingAssumption(assumption),
      openReview: assumption => navigate(getReviewAssumptionUrl(projectId, assumption.id)),
      openConfirm: handleConfirm,
    }),
    [handleConfirm, navigate, getReviewAssumptionUrl, projectId],
  );

  const closeCreate = useCallback(() => setIsCreateOpen(false), []);
  const closeUpdate = useCallback(() => setEditingAssumption(null), []);

  return (
    <AssumptionActionsContext.Provider value={value}>
      {children}
      <CreateAssumptionModal projectId={projectId} isOpen={isCreateOpen} onClose={closeCreate} onSaved={onSaved} />
      <UpdateAssumptionModal
        assumption={editingAssumption}
        isOpen={!!editingAssumption}
        onClose={closeUpdate}
        onSaved={onSaved}
      />
    </AssumptionActionsContext.Provider>
  );
};

export const useAssumptionActions = (): AssumptionActionsContextValue => {
  const ctx = useContext(AssumptionActionsContext);
  if (!ctx) {
    throw new Error('useAssumptionActions must be used within an AssumptionActionsProvider');
  }
  return ctx;
};
